import { useEffect, useRef, useState } from 'react';

import { Button, HStack, Popover, Portal } from '@chakra-ui/react';

import { IconsLinear } from '@nocowanie/icons/linear';

import { TooltipProps } from './tooltip.props';

import { useIsMobile } from '../../../helpers/is-mobile';

export const Tooltip = ({
    title,
    children,
    autoCloseDelay,
    buttonProps = {},
    buttonIconProps = {},
    bodyProps = {},
    extendedTriggerArea,
    width,
    maxW,
    showTriggerIcon = true,
    ...rest
}: TooltipProps): JSX.Element => {
    const isMobileBrowser = useIsMobile();

    const [isOpen, setIsOpen] = useState(false);
    const [autoCloseTimeout, setAutoCloseTimeout] = useState<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const popoverRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleTouchMove = (event: TouchEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            window.addEventListener('touchmove', handleTouchMove);
        }

        return () => {
            window.removeEventListener('touchmove', handleTouchMove);
        };
    }, [isOpen]);

    const tooltipTrigger = (
        <Button
            alignItems={'center'}
            ml={2}
            variant={'link'}
            colorPalette={'gray'}
            data-testid={'tooltip-trigger'}
            {...buttonProps}
        >
            <IconsLinear.InfoCircle {...buttonIconProps} />
        </Button>
    );

    const onClose = () => {
        setIsOpen(false);

        if (autoCloseDelay && autoCloseTimeout !== null) {
            clearTimeout(autoCloseTimeout);
            setAutoCloseTimeout(null);
        }
    };

    const onOpen = () => {
        setIsOpen(true);

        if (autoCloseDelay) {
            if (autoCloseTimeout !== null) {
                clearTimeout(autoCloseTimeout);
            }

            const timeout = setTimeout(() => {
                onClose();
            }, autoCloseDelay);
            setAutoCloseTimeout(timeout);
        }
    };

    return (
        <Popover.Root
            unmountOnExit={true}
            portalled={true}
            lazyMount={true}
            open={isOpen}
            onOpenChange={details => setTimeout(() => (details.open ? onOpen?.() : onClose()), 0)}
            {...rest}
        >
            <Popover.Trigger asChild>
                {extendedTriggerArea ? (
                    <HStack {...extendedTriggerArea.wrapperProps}>
                        {extendedTriggerArea.additionalTrigger}
                        {showTriggerIcon ? tooltipTrigger : null}
                    </HStack>
                ) : (
                    tooltipTrigger
                )}
            </Popover.Trigger>
            <Portal>
                <Popover.Positioner>
                    <Popover.Content
                        pr={isMobileBrowser ? 5 : undefined}
                        width={width}
                        maxW={maxW ?? '280px'}
                        ref={popoverRef}
                        data-testid={'tooltip'}
                        css={{
                            '--popover-size': 'auto',
                            '--popover-padding': '{sizes.2}',
                            '--popover-bg': '{colors.gray.950}',
                            color: 'white',
                        }}
                    >
                        {isMobileBrowser ? <Popover.CloseTrigger /> : null}
                        <Popover.Arrow>
                            <Popover.ArrowTip border={0} />
                        </Popover.Arrow>
                        <Popover.Body fontSize={'sm'} textAlign={'center'} {...bodyProps}>
                            {title ? (
                                <Popover.Title
                                    fontWeight={700}
                                    borderBottom={0}
                                    fontSize={'sm'}
                                    pb={0}
                                    data-testid={'tooltip-title'}
                                >
                                    {title}
                                </Popover.Title>
                            ) : null}
                            {children}
                        </Popover.Body>
                    </Popover.Content>
                </Popover.Positioner>
            </Portal>
        </Popover.Root>
    );
};
